import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { formatMoney } from '@theme/money-formatting';

/** Line item property tagging a rush line with the tier that created it. */
const TIER_KEY = '_rush_tier';
const LABEL_KEY = '_rush_label';

/** Minimum gap between recalculations, and the debounce applied inside it. */
const RECALC_COOLDOWN = 2000;
const RECALC_DEBOUNCE = 300;

/**
 * How long our own writes keep suppressing the cart-update listener. The theme
 * dispatches cart:update from the morph our own write triggers, so the flag has to
 * outlive the whole sequence rather than just the fetch.
 */
const OWN_WRITE_GRACE = 1000;

/**
 * Rush processing upsell: a dropdown that charges a percentage of the cart subtotal
 * as line items of a dedicated product.
 *
 * Ported from the stickerbros-rush-order theme app block, which Rebuy can't render.
 * Most of that extension was scaffolding an app block needs and a theme doesn't —
 * lazy-loading, blind DOM injection with a re-inject observer, and fetch/XHR
 * monkey-patching to notice cart changes. Here the element is rendered where it
 * belongs, and `cart:update` is a real event, so all of that is gone. What remains is
 * the tier maths, the variant lookup, and the recalculation guard.
 *
 * Config comes entirely from window.SBCartExtras.rushOrder, published by
 * snippets/custom-cart-extras from theme settings. Deliberately not attributes: there
 * are a dozen values plus a variant table, and a Rebuy Smart Cart template — the
 * reason this exists — can't render Liquid into attributes anyway. Both hosts emit a
 * bare <cart-rush-order> and get the same thing.
 *
 * Shopify can't charge an arbitrary amount, so the fee is line items of the rush
 * product, whose variants are priced in fixed increments. Selecting a tier removes any
 * existing rush lines and adds the ones matching the current subtotal; changing the
 * cart afterwards recalculates them.
 *
 * @typedef {{ id: string, label: string, percentage: number, description: string }} Tier
 * @typedef {{ productId: number, tiers: Tier[], variants: [number, number][], heading: string,
 *             defaultLabel: string, defaultDescription: string, disclaimer: string }} RushConfig
 *
 * @extends Component
 */
export class CartRushOrder extends Component {
  /** Subtotal in cents, excluding rush lines — what the percentages apply to. */
  #subtotal = 0;

  /** @type {string | null} */
  #tierId = null;

  /** @type {string[]} Cart line keys of the rush lines currently in the cart. */
  #rushKeys = [];

  /**
   * False until the first cart read lands.
   *
   * Every price in the dropdown is a percentage of the subtotal, so before that read
   * there is nothing truthful to show — the tiers would all read +$0.00 and the default
   * option would advertise "starting at $0.00". The extension hid its container until
   * init() succeeded for the same reason; #paint keeps the element hidden instead.
   */
  #ready = false;

  /**
   * Whether the menu is open, held here rather than read back off the class.
   *
   * The markup is rebuildable — a cart morph strips it, and a host framework may
   * re-render over it — so anything stored only in the DOM is lost on a rebuild. Open
   * state in particular: losing it mid-interaction makes the dropdown look broken,
   * because the click that opened it is followed by a rebuild that closes it.
   */
  #open = false;

  #busy = false;
  #ownWrite = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #ownWriteTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #recalcTimer;
  #lastRecalc = 0;

  /** Rebuilds the markup when a cart morph strips it. See cart-attribute-checkbox. */
  #childListObserver = new MutationObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#childListObserver.observe(this, { childList: true });
    // One listener covers additions, quantity changes and removals: CartAddEvent and
    // CartUpdateEvent both dispatch ThemeEvents.cartUpdate, and both bubble to
    // document. This is what replaces the extension's fetch/XHR patching.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener('click', this.#handleOutsideClick);
    // Delegated from the host, never bound to the trigger or the options. Those nodes
    // are rebuildable, and a host that re-inserts them — a clone, or an ancestor's
    // innerHTML being re-set — brings back markup without listeners, which #render
    // won't notice because the markup is present. The host is the custom element
    // itself, so this is reattached by connectedCallback whenever it is re-created.
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#childListObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('click', this.#handleOutsideClick);
    clearTimeout(this.#recalcTimer);
    clearTimeout(this.#ownWriteTimer);
  }

  /** @returns {RushConfig | null} */
  get config() {
    const extras = /** @type {{ rushOrder?: RushConfig } | undefined} */ (Reflect.get(window, 'SBCartExtras'));
    const config = extras?.rushOrder ?? null;

    return config?.productId && config.tiers?.length && config.variants?.length ? config : null;
  }

  /**
   * Builds the dropdown. No-op once built, so the observer can call it freely.
   *
   * Only the shell is built here — everything that depends on cart state is written by
   * #paint, which runs again on every refresh without discarding the nodes.
   */
  #render() {
    if (this.querySelector('.cart-rush-order__dropdown')) return;

    const config = this.config;
    if (!config) return;

    this.textContent = '';

    if (config.heading) {
      const heading = document.createElement('h3');
      heading.className = 'cart-rush-order__heading';
      heading.textContent = config.heading;
      this.append(heading);
    }

    const dropdown = document.createElement('div');
    dropdown.className = 'cart-rush-order__dropdown';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cart-rush-order__trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'listbox');

    const menu = document.createElement('div');
    menu.className = 'cart-rush-order__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    dropdown.append(trigger, menu);
    this.append(dropdown);

    if (config.disclaimer) {
      const disclaimer = document.createElement('p');
      disclaimer.className = 'cart-rush-order__disclaimer';
      disclaimer.textContent = config.disclaimer;
      disclaimer.hidden = true;
      this.append(disclaimer);
    }

    this.#paint();
    // A rebuild starts closed; put it back the way the customer left it.
    this.#setOpen(this.#open);
  }

  /**
   * Writes the current state into the built markup: each tier's price against the
   * current subtotal, which option is selected, and whether the disclaimer shows.
   */
  #paint() {
    const config = this.config;
    const trigger = this.querySelector('.cart-rush-order__trigger');
    const menu = this.querySelector('.cart-rush-order__menu');
    if (!config || !(trigger instanceof HTMLElement) || !(menu instanceof HTMLElement)) return;

    const priced = config.tiers.map((tier) => ({ tier, cents: this.#feeFor(tier) }));
    const cheapest = priced.reduce(
      (min, entry) => (entry.cents > 0 && entry.cents < min ? entry.cents : min),
      Number.POSITIVE_INFINITY
    );
    const startingAt = this.#money(Number.isFinite(cheapest) ? cheapest : 0);
    const defaultDescription = (config.defaultDescription || '').replace('{starting_at}', startingAt);

    const selected = priced.find((entry) => entry.tier.id === this.#tierId) ?? null;

    trigger.textContent = '';
    trigger.append(
      this.#line(
        selected ? selected.tier.label : config.defaultLabel,
        selected ? `+${this.#money(selected.cents)}` : '',
        'cart-rush-order__trigger-label'
      ),
      this.#caret()
    );

    menu.textContent = '';
    menu.append(this.#option('', config.defaultLabel, '', defaultDescription));

    for (const { tier, cents } of priced) {
      const price = `+${this.#money(cents)}${config.disclaimer ? '*' : ''}`;
      menu.append(this.#option(tier.id, tier.label, price, tier.description));
    }

    const disclaimer = this.querySelector('.cart-rush-order__disclaimer');
    if (disclaimer instanceof HTMLElement) disclaimer.hidden = !selected;

    // Hidden until the cart is known, and whenever there is nothing to take a
    // percentage of. A zero subtotal happens transiently when the last real item is
    // removed while a tier is selected — #recalc strips the rush lines, and until it
    // does, offering "rush processing from $0.00" is worse than showing nothing.
    this.hidden = !this.#ready || this.#subtotal <= 0;
  }

  /**
   * @param {string} tierId - Empty for the "no rush" option.
   * @param {string} label
   * @param {string} price
   * @param {string} description
   */
  #option(tierId, label, price, description) {
    const selected = tierId === (this.#tierId ?? '');

    const option = document.createElement('button');
    option.type = 'button';
    option.className = `cart-rush-order__option${selected ? ' cart-rush-order__option--selected' : ''}`;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(selected));
    option.dataset.tierId = tierId;
    option.append(this.#line(label, price, 'cart-rush-order__option-label'));

    if (description) {
      const desc = document.createElement('span');
      desc.className = 'cart-rush-order__option-description';
      desc.textContent = description;
      option.append(desc);
    }

    return option;
  }

  /**
   * @param {string} label
   * @param {string} price
   * @param {string} className
   */
  #line(label, price, className) {
    const row = document.createElement('span');
    row.className = className;

    const name = document.createElement('span');
    name.textContent = label;
    row.append(name);

    if (price) {
      const amount = document.createElement('span');
      amount.className = 'cart-rush-order__price';
      amount.textContent = price;
      row.append(amount);
    }

    return row;
  }

  #caret() {
    const caret = document.createElement('span');
    caret.className = 'cart-rush-order__caret';
    caret.setAttribute('aria-hidden', 'true');

    return caret;
  }

  /** @param {boolean} open */
  #setOpen(open) {
    this.#open = open;

    const dropdown = this.querySelector('.cart-rush-order__dropdown');
    const trigger = this.querySelector('.cart-rush-order__trigger');
    const menu = this.querySelector('.cart-rush-order__menu');
    if (!(dropdown instanceof HTMLElement) || !(trigger instanceof HTMLElement) || !(menu instanceof HTMLElement)) {
      return;
    }

    dropdown.classList.toggle('cart-rush-order__dropdown--open', open);
    trigger.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
  }

  #toggle = () => this.#setOpen(!this.#open);

  #close() {
    if (this.#open) this.#setOpen(false);
  }

  /**
   * Routes a click to the trigger or to an option.
   *
   * `closest` rather than a target match, because both are built from nested spans and
   * the click lands on whichever one the customer hit.
   *
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.cart-rush-order__trigger')) {
      if (!this.#busy) this.#toggle();
      return;
    }

    const option = target.closest('.cart-rush-order__option');
    if (option instanceof HTMLElement) this.#select(option.dataset.tierId ?? '');
  };

  /**
   * Closes the menu on a click anywhere outside it.
   *
   * On document rather than the host, because the point is the clicks the host never
   * sees. Bound for as long as the element is connected.
   *
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    // composedPath() is fixed at dispatch, so it still contains this element even if a
    // host framework has re-rendered our subtree by the time this runs. `contains` on a
    // detached target would read as a click from outside and close what just opened.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this)) return;

    const target = event.target;
    if (!path.length && target instanceof Node && this.contains(target)) return;

    this.#close();
  };

  /**
   * Escape closes, arrows walk the options — the keyboard contract a listbox owes, and
   * the only way to use this without a pointer.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
      this.#trigger()?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();

    const options = [...this.querySelectorAll('.cart-rush-order__option')];
    if (!options.length) return;

    if (!this.#open) {
      this.#setOpen(true);
      /** @type {HTMLElement} */ (options[0]).focus();
      return;
    }

    const index = options.indexOf(/** @type {Element} */ (document.activeElement));
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;

    if (next < 0) {
      this.#trigger()?.focus();
      return;
    }

    /** @type {HTMLElement | undefined} */ (options[Math.min(next, options.length - 1)])?.focus();
  };

  /** @returns {HTMLElement | null} */
  #trigger() {
    const trigger = this.querySelector('.cart-rush-order__trigger');

    return trigger instanceof HTMLElement ? trigger : null;
  }

  /**
   * The fee this tier costs against the current subtotal, snapped to what the rush
   * product can actually charge — so the price shown is the price added.
   *
   * @param {Tier} tier
   */
  #feeFor(tier) {
    const target = Math.round((this.#subtotal * tier.percentage) / 100);

    return this.#linesFor(target).reduce((total, line) => total + line.price * line.quantity, 0);
  }

  /**
   * The rush lines that come closest to `targetCents`.
   *
   * Picks from the variant table Liquid emitted rather than computing variant ids
   * arithmetically, as the extension did: that assumed ids stay contiguous forever and
   * would happily produce an id for a price the product doesn't sell, which fails the
   * add with nothing shown to the customer.
   *
   * The table is what Liquid could see, and Shopify caps that — so above its top price
   * the largest variant is stacked by quantity instead. Slightly untidy in the order,
   * and always chargeable.
   *
   * @param {number} targetCents
   * @returns {{ id: number, quantity: number, price: number }[]}
   */
  #linesFor(targetCents) {
    const variants = this.config?.variants ?? [];
    if (targetCents <= 0 || !variants.length) return [];

    const [maxPrice, maxId] = variants[variants.length - 1];

    if (targetCents > maxPrice) {
      const quantity = Math.floor(targetCents / maxPrice);
      const lines = [{ id: maxId, quantity, price: maxPrice }];
      const remainder = targetCents - quantity * maxPrice;
      const closest = this.#closestVariant(remainder);

      if (closest && remainder >= variants[0][0] / 2) lines.push(closest);

      return lines;
    }

    const closest = this.#closestVariant(targetCents);

    return closest ? [closest] : [];
  }

  /**
   * @param {number} targetCents
   * @returns {{ id: number, quantity: number, price: number } | null}
   */
  #closestVariant(targetCents) {
    let best = null;

    for (const [price, id] of this.config?.variants ?? []) {
      if (best === null || Math.abs(price - targetCents) < Math.abs(best.price - targetCents)) {
        best = { id, quantity: 1, price };
      }
    }

    return best;
  }

  /** @param {number} cents */
  #money(cents) {
    const extras = /** @type {{ moneyFormat?: string, currency?: string } | undefined} */ (
      Reflect.get(window, 'SBCartExtras')
    );

    return formatMoney(cents, extras?.moneyFormat || '${{amount}}', extras?.currency || 'USD');
  }

  /** Reads the cart and repaints: the subtotal, the selected tier and the rush keys all live there. */
  async #refresh() {
    if (!this.config) return;

    try {
      this.#adopt(await this.#cart());
      this.#ready = true;
      this.#paint();
    } catch (error) {
      console.error('[cart-rush-order] could not read the cart', error);
    }
  }

  async #cart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) throw new Error(`Cart read failed with ${response.status}`);

    return response.json();
  }

  /**
   * Derives our whole state from a cart payload. The cart is the only store — a page
   * reload or a second cart surface picks the same state back up.
   *
   * @param {{ items?: { key: string, product_id: number, final_line_price: number, properties?: Record<string, string> }[] }} cart
   */
  #adopt(cart) {
    const productId = this.config?.productId;
    const items = cart.items ?? [];

    this.#subtotal = items
      .filter((item) => item.product_id !== productId)
      .reduce((total, item) => total + item.final_line_price, 0);

    this.#rushKeys = items.filter((item) => item.product_id === productId).map((item) => item.key);
    this.#tierId = items.map((item) => item.properties?.[TIER_KEY]).find(Boolean) ?? null;
  }

  /**
   * Applies a choice: clear the old rush lines, add the new ones, reconcile.
   *
   * Painted optimistically first — the subtotal is already known, so the price the
   * customer just clicked can be shown immediately rather than after two round trips.
   *
   * @param {string} tierId - Empty for the "no rush" option.
   */
  async #select(tierId) {
    if (this.#busy || !this.config) return;

    this.#busy = true;
    this.#markOwnWrite();
    this.#close();
    this.setAttribute('aria-busy', 'true');

    const tier = this.config.tiers.find((candidate) => candidate.id === tierId) ?? null;
    this.#tierId = tier?.id ?? null;
    this.#paint();

    try {
      let result = await this.#removeRush(this.#rushKeys);
      this.#rushKeys = [];

      if (tier) {
        const lines = this.#linesFor(Math.round((this.#subtotal * tier.percentage) / 100));
        if (lines.length) result = (await this.#addRush(lines, tier)) ?? result;
      }

      this.#adopt(await this.#cart());
      this.#ready = true;
      this.#paint();
      await this.#renderThemeCart(result);
    } catch (error) {
      console.error('[cart-rush-order] could not apply the rush tier', error);
      // The cart is the source of truth, so re-reading it is also the rollback.
      await this.#refresh();
    } finally {
      this.removeAttribute('aria-busy');
      this.#busy = false;
    }
  }

  /**
   * @param {{ id: number, quantity: number, price: number }[]} lines
   * @param {Tier} tier
   */
  async #addRush(lines, tier) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        items: lines.map((line) => ({
          id: line.id,
          quantity: line.quantity,
          properties: { [TIER_KEY]: tier.id, [LABEL_KEY]: tier.label },
        })),
        sections: this.#sectionId(),
      }),
    });

    // cart_add_url already carries the .js suffix (snippets/scripts.liquid); the others don't.
    const response = await fetch(Theme.routes.cart_add_url, config);
    if (!response.ok) throw new Error(`Rush add failed with ${response.status}: ${await response.text()}`);

    const result = await response.json();
    this.#verifyCharge(result, lines);

    return result;
  }

  /**
   * Warns when what the cart charged isn't what we quoted.
   *
   * The variant table can go stale — a variant deleted or repriced behind our back — and
   * the failure mode is silent: the add succeeds, the customer is charged, and only the
   * amount is wrong. Loud in the console beats invisible.
   *
   * @param {{ items?: { final_line_price?: number }[] }} result
   * @param {{ price: number, quantity: number }[]} lines
   */
  #verifyCharge(result, lines) {
    const quoted = lines.reduce((total, line) => total + line.price * line.quantity, 0);
    const added = (result.items ?? []).reduce((total, item) => total + (item.final_line_price ?? 0), 0);

    if (added && added !== quoted) {
      console.error(`[cart-rush-order] charged ${added} but quoted ${quoted} — the variant table may be stale`);
    }
  }

  /** @param {string[]} keys */
  async #removeRush(keys) {
    if (!keys.length) return null;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    const config = fetchConfig('json', {
      body: JSON.stringify({ updates, sections: this.#sectionId() }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    if (!response.ok) throw new Error(`Rush removal failed with ${response.status}: ${await response.text()}`);

    return response.json();
  }

  /**
   * A cart change from anywhere else: recalculate, because the fee is a percentage of a
   * subtotal that just moved.
   *
   * Our own writes reach this too — the morph we trigger dispatches cart:update — so
   * they're filtered by source and by the write flag.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const source = /** @type {{ detail?: { data?: { source?: string } } }} */ (event).detail?.data?.source;
    if (source === 'cart-rush-order' || this.#ownWrite || this.#busy) return;

    clearTimeout(this.#recalcTimer);
    const sinceLast = Date.now() - this.#lastRecalc;
    const delay = sinceLast < RECALC_COOLDOWN ? RECALC_COOLDOWN - sinceLast : RECALC_DEBOUNCE;

    // Debounced and rate-limited: holding a quantity stepper fires this repeatedly, and
    // each recalculation is a remove plus an add.
    this.#recalcTimer = setTimeout(() => this.#recalc(), delay);
  };

  /**
   * Re-prices the selected tier against the new subtotal.
   *
   * Nothing to do when no tier is selected, or when the subtotal hasn't actually moved —
   * a cart update that only changed a property still lands here.
   */
  async #recalc() {
    if (this.#busy || !this.config) return;

    this.#lastRecalc = Date.now();
    const previous = this.#subtotal;

    try {
      this.#adopt(await this.#cart());
    } catch (error) {
      console.error('[cart-rush-order] could not read the cart', error);
      return;
    }

    const tier = this.config.tiers.find((candidate) => candidate.id === this.#tierId) ?? null;

    // Rush lines that can no longer be priced have to go, not just be left alone.
    //
    // Two ways to get here. The subtotal is zero: the customer removed the last real
    // item, and because the rush rows are hidden from the cart they'd be looking at an
    // item-less cart with a subtotal and a live checkout button — able to buy nothing
    // but a rush fee. Or no configured tier matches the `_rush_tier` on the line, after
    // a tier was renamed or removed in theme settings: the fee keeps being charged while
    // the dropdown reads "no rush processing", with no way for the customer to clear it.
    if (this.#rushKeys.length && (!tier || this.#subtotal <= 0)) {
      await this.#select('');
      return;
    }

    if (!tier || this.#subtotal === previous) {
      this.#paint();
      return;
    }

    await this.#select(tier.id);
  }

  /** Keeps our own cart writes from being read as somebody else's. */
  #markOwnWrite() {
    this.#ownWrite = true;
    clearTimeout(this.#ownWriteTimer);
    this.#ownWriteTimer = setTimeout(() => {
      this.#ownWrite = false;
    }, OWN_WRITE_GRACE);
  }

  /** @returns {string | undefined} */
  #sectionId() {
    return this.#cartItems()?.sectionId;
  }

  /**
   * Repaints the theme cart the way a quantity change does. No-op in a Rebuy cart,
   * which has no theme section — it refreshes from the rebuy:cart.change listener in
   * snippets/custom-cart-extras instead.
   *
   * @param {{ item_count?: number, sections?: Record<string, string> } | null} result
   */
  async #renderThemeCart(result) {
    const cartItems = this.#cartItems();
    const sectionId = cartItems?.sectionId;
    if (!cartItems || !sectionId || !result) return;

    cartItems.dispatchEvent(
      new CartUpdateEvent(result, sectionId, {
        itemCount: result.item_count,
        source: 'cart-rush-order',
        sections: result.sections,
      })
    );

    const html = result.sections?.[sectionId];
    if (html) await morphSection(sectionId, html, cartItems.isDrawer ? 'hydration' : 'full');
  }

  /** @returns {(HTMLElement & { sectionId: string, isDrawer: boolean }) | null} */
  #cartItems() {
    const element = this.closest('cart-items-component');
    if (!(element instanceof HTMLElement) || !('sectionId' in element)) return null;

    return /** @type {HTMLElement & { sectionId: string, isDrawer: boolean }} */ (element);
  }
}

if (!customElements.get('cart-rush-order')) {
  customElements.define('cart-rush-order', CartRushOrder);
}
